/**
 * PlantasIotTankHistoryModal.jsx  v1
 * Historial de un tanque (base plantas_iot) — mismo formato que TankHistoryModal de Alvear
 * Pestañas: Alarmas · Nivel % · Toneladas
 */

import { useState, useEffect } from 'react';
import {
    Box, Chip, CircularProgress,
    Dialog, DialogContent, DialogTitle,
    Grid, IconButton, Paper, Stack, Tab, Tabs,
    Typography, alpha, useTheme,
} from '@mui/material';
import {
    Close as CloseIcon,
    BarChart as BarChartIcon,
    CalendarMonth as CalendarIcon,
    NotificationsActive as AlarmIcon,
} from '@mui/icons-material';
import { LineChart } from '@mui/x-charts/LineChart';
import dayjs from 'dayjs';
import RangoFechas, { mesActual } from '../alvear/Rangofechas';

function fmtNum(v, dec = 2) {
    if (v === null || v === undefined || isNaN(v)) return '—';
    return Number(v).toLocaleString('es-AR', { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

// Color de la alarma según su tipo: HH / LL críticas, H / L avisos
function severidad(tipo) {
    const t = (tipo || '').toUpperCase();
    if (t.endsWith('_HH') || t.endsWith('_LL')) return 'error';
    if (t.endsWith('_H') || t.endsWith('_L')) return 'warning';
    return 'info';
}

export default function PlantasIotTankHistoryModal({ open, tanque, plantaId, color, apiUrl, onClose }) {
    const theme = useTheme();
    const [tab, setTab] = useState(0);
    const [rango, setRango] = useState(() => mesActual());
    const [historico, setHistorico] = useState([]);
    const [alarmas, setAlarmas] = useState([]);
    const [loadingHis, setLoadingHis] = useState(false);
    const [loadingAl, setLoadingAl] = useState(false);

    const headers = { 'x-api-key': process.env.REACT_APP_HMI_API_KEY || '' };
    const base = `${apiUrl}/api/plantas-iot/${plantaId}`;

    // Reset al abrir
    useEffect(() => {
        if (open) {
            setTab(0);
            setRango(mesActual());
        }
    }, [open]);

    // Historial (nivel y masa)
    useEffect(() => {
        if (!open || !tanque || !plantaId) return;
        let activo = true;
        setLoadingHis(true);
        fetch(`${base}/history?from=${encodeURIComponent(rango.from)}&to=${encodeURIComponent(rango.to)}&tanqueId=${tanque.tanque_id}&puntos=300`, { headers })
            .then((r) => r.json())
            .then((d) => {
                if (!activo) return;
                const puntos = (d.puntos || []).map((p) => ({
                    fecha: dayjs(p.ts).format('DD/MM HH:mm'),
                    pct: p.volumen_pct,
                    tn: p.masa_kg !== null ? Number((p.masa_kg / 1000).toFixed(2)) : null,
                }));
                setHistorico(puntos);
            })
            .catch(() => activo && setHistorico([]))
            .finally(() => activo && setLoadingHis(false));
        return () => { activo = false; };
    }, [open, tanque, plantaId, rango, apiUrl]);

    // Alarmas del tanque
    useEffect(() => {
        if (!open || !tanque || !plantaId) return;
        let activo = true;
        setLoadingAl(true);
        fetch(`${base}/eventos?from=${encodeURIComponent(rango.from)}&to=${encodeURIComponent(rango.to)}&tanqueId=${tanque.tanque_id}&limit=500`, { headers })
            .then((r) => r.json())
            .then((d) => activo && setAlarmas(d.eventos || []))
            .catch(() => activo && setAlarmas([]))
            .finally(() => activo && setLoadingAl(false));
        return () => { activo = false; };
    }, [open, tanque, plantaId, rango, apiUrl]);

    if (!tanque) return null;

    // Balance del período: primer y último valor de toneladas
    const conTn = historico.filter((p) => p.tn !== null);
    const balance = conTn.length > 1
        ? {
            inicio: conTn[0].tn,
            fin: conTn[conTn.length - 1].tn,
            delta: Number((conTn[conTn.length - 1].tn - conTn[0].tn).toFixed(2)),
        }
        : null;

    const tickCada = Math.max(1, Math.ceil(historico.length / 8));

    const graficoVacio = (
        <Stack alignItems="center" py={4}>
            <Typography variant="body2" color="text.disabled">Sin datos en el período</Typography>
        </Stack>
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
            PaperProps={{ sx: { borderRadius: 2, maxHeight: '92vh' } }}>

            {/* Header */}
            <DialogTitle sx={{ pb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color, flexShrink: 0, mt: 0.5 }} />
                        <Box>
                            <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
                                {tanque.tanque} — Historial
                            </Typography>
                            <Typography variant="caption" color="text.disabled" sx={{ textTransform: 'capitalize' }}>
                                {tanque.producto || '—'}
                            </Typography>
                        </Box>
                    </Stack>
                    <IconButton size="small" onClick={onClose} sx={{ flexShrink: 0 }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Stack>

                <Box sx={{ mt: 1.5 }}>
                    <RangoFechas value={rango} onChange={setRango} />
                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
                <Tabs value={tab} onChange={(_, v) => setTab(v)}
                    sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}>
                    <Tab icon={<AlarmIcon sx={{ fontSize: 15 }} />} iconPosition="start" label="Alarmas" sx={{ fontSize: 12, minHeight: 42 }} />
                    <Tab icon={<BarChartIcon sx={{ fontSize: 15 }} />} iconPosition="start" label="Nivel %" sx={{ fontSize: 12, minHeight: 42 }} />
                    <Tab icon={<CalendarIcon sx={{ fontSize: 15 }} />} iconPosition="start" label="Toneladas" sx={{ fontSize: 12, minHeight: 42 }} />
                </Tabs>

                <Box sx={{ p: 2, overflowY: 'auto', maxHeight: '55vh' }}>

                    {/* TAB 0 — ALARMAS */}
                    {tab === 0 && (
                        <>
                            {balance && (
                                <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
                                    <Typography variant="caption" color="text.disabled"
                                        sx={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', mb: 1 }}>
                                        Balance del período
                                    </Typography>
                                    <Grid container spacing={2}>
                                        {[
                                            { label: 'Inicio', val: `${fmtNum(balance.inicio)} tn` },
                                            { label: 'Fin', val: `${fmtNum(balance.fin)} tn` },
                                            {
                                                label: 'Variación',
                                                val: `${balance.delta > 0 ? '+' : ''}${fmtNum(balance.delta)} tn`,
                                                color: balance.delta > 0 ? 'success.main' : balance.delta < 0 ? 'error.main' : 'text.primary',
                                            },
                                        ].map((s, i) => (
                                            <Grid item xs={4} key={i}>
                                                <Typography variant="caption" color="text.disabled" sx={{ fontSize: 9, display: 'block' }}>{s.label}</Typography>
                                                <Typography sx={{ fontFamily: "'Orbitron', sans-serif", fontSize: 15, fontWeight: 700, color: s.color || 'text.primary' }}>
                                                    {s.val}
                                                </Typography>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Paper>
                            )}

                            {loadingAl ? (
                                <Stack alignItems="center" py={4}>
                                    <CircularProgress size={28} />
                                    <Typography variant="caption" color="text.disabled" mt={1}>Cargando alarmas...</Typography>
                                </Stack>
                            ) : alarmas.length === 0 ? (
                                <Stack alignItems="center" py={4}>
                                    <Typography variant="body2" color="text.disabled">Sin alarmas en el período</Typography>
                                </Stack>
                            ) : (
                                <Stack spacing={1}>
                                    {alarmas.map((a) => {
                                        const sev = severidad(a.tipo);
                                        const pal = theme.palette[sev];
                                        return (
                                            <Paper key={a.id} variant="outlined" sx={{ p: 1.5, borderColor: alpha(pal.main, 0.3) }}>
                                                <Stack direction="row" alignItems="center" spacing={1.5}>
                                                    <AlarmIcon sx={{ color: `${sev}.main`, fontSize: 20 }} />
                                                    <Box>
                                                        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                                                            <Chip size="small" label={a.tipo}
                                                                sx={{
                                                                    height: 20, fontSize: 9, fontWeight: 700,
                                                                    bgcolor: alpha(pal.main, 0.12), color: `${sev}.main`,
                                                                    '& .MuiChip-label': { px: 1 },
                                                                }}
                                                            />
                                                            <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>
                                                                {dayjs(a.ts).format('DD/MM/YYYY HH:mm:ss')}
                                                            </Typography>
                                                        </Stack>
                                                        {a.detalle && (
                                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                                                                {a.detalle}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Stack>
                                            </Paper>
                                        );
                                    })}
                                </Stack>
                            )}
                        </>
                    )}

                    {/* TAB 1 — NIVEL % */}
                    {tab === 1 && (
                        loadingHis ? (
                            <Stack alignItems="center" py={4}><CircularProgress size={28} /></Stack>
                        ) : historico.length === 0 ? graficoVacio : (
                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                <Typography variant="caption" fontWeight={600} fontSize={11} display="block" mb={1}>
                                    Nivel %
                                </Typography>
                                <LineChart
                                    dataset={historico}
                                    series={[{ dataKey: 'pct', label: tanque.tanque, color, showMark: false, curve: 'monotoneX', connectNulls: false }]}
                                    xAxis={[{
                                        dataKey: 'fecha', scaleType: 'band', tickLabelStyle: { fontSize: 8 },
                                        tickInterval: (_, i) => i % tickCada === 0,
                                    }]}
                                    yAxis={[{ min: 0, max: 100, tickLabelStyle: { fontSize: 9 }, label: '%' }]}
                                    height={280}
                                    margin={{ left: 40, right: 16, top: 8, bottom: 32 }}
                                    slotProps={{ legend: { hidden: true } }}
                                />
                            </Paper>
                        )
                    )}

                    {/* TAB 2 — TONELADAS */}
                    {tab === 2 && (
                        loadingHis ? (
                            <Stack alignItems="center" py={4}><CircularProgress size={28} /></Stack>
                        ) : historico.length === 0 ? graficoVacio : (
                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                <Typography variant="caption" fontWeight={600} fontSize={11} display="block" mb={1}>
                                    Toneladas disponibles
                                </Typography>
                                <LineChart
                                    dataset={historico}
                                    series={[{ dataKey: 'tn', label: `${tanque.tanque} (tn)`, color, showMark: false, curve: 'monotoneX', connectNulls: false }]}
                                    xAxis={[{
                                        dataKey: 'fecha', scaleType: 'band', tickLabelStyle: { fontSize: 8 },
                                        tickInterval: (_, i) => i % tickCada === 0,
                                    }]}
                                    yAxis={[{ tickLabelStyle: { fontSize: 9 }, label: 'tn' }]}
                                    height={280}
                                    margin={{ left: 50, right: 16, top: 8, bottom: 32 }}
                                    slotProps={{ legend: { hidden: true } }}
                                />
                            </Paper>
                        )
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
}